import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useStore } from "../../context/StoreContext";
import Alert from "../../components/common/Alert";
import Badge from "../../components/common/Badge";
import Button from "../../components/common/Button";
import Card from "../../components/common/Card";
import Input from "../../components/common/Input";
import Modal from "../../components/common/Modal";
import PageHeader from "../../components/common/PageHeader";
import Select from "../../components/common/Select";
import Table from "../../components/common/Table";
import Textarea from "../../components/common/Textarea";
import Tabs from "../../components/common/Tabs";
import useCurrentOperator from "../../hooks/useCurrentOperator";
import {
  useGetReconciliationQuery,
  useCreateCashMovementMutation,
  useGetCashMovementsQuery,
  useGetTillSessionQuery,
  useGetTillsQuery,
  useGetUsersQuery,
  useRecalculateReconciliationMutation,
  useCloseTillSessionMutation,
  useSaveReconciliationLinesMutation,
} from "../../store/api";
import { usePosRealtime } from "../../hooks/usePosRealtime";
import {
  formatCurrency,
  getCashingUpApprovalStatusLabel,
  getCashingUpMovementTypeLabel,
  getCashingUpSessionStatusLabel,
  getCashingUpStatusTone,
  getMovementSignedAmount,
  isVisibleCashMovementStatus,
  getTenderSummaryAmount,
} from "../../utils/cashingUp";

const getErrorMessage = (err, fallback) =>
  err?.data?.detail ||
  err?.data?.message ||
  err?.message ||
  fallback ||
  "Something went wrong.";

const SessionDetailCard = ({ label, value, helperText = "" }) => (
  <div className="rounded-xl border border-slate-200 p-4 dark:border-slate-700">
    <p className="flex items-baseline gap-1 whitespace-nowrap text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">
      <span className="shrink-0">{label}</span>
      {helperText ? (
        <span className="shrink-0 text-[9px] font-medium normal-case tracking-normal text-slate-400 dark:text-slate-500">
          ({helperText})
        </span>
      ) : null}
    </p>
    <p className="mt-1 text-sm font-semibold text-slate-900 dark:text-slate-100">
      {value}
    </p>
  </div>
);

const movementInitialForm = {
  movementType: "",
  amount: "",
  reasonCode: "",
};

const reconciliationLineFromTender = (tender, index) => ({
  reconciliationLineId: tender?.reconciliationLineId || null,
  tenderType: tender?.tenderType || "CASH",
  providerOrChannel: tender?.providerOrChannel || "",
  actualAmount:
    tender?.actualAmount !== undefined && tender?.actualAmount !== null
      ? String(tender.actualAmount)
      : "",
  settlementRef: tender?.settlementRef || "",
  slipAttachmentRef: tender?.slipAttachmentRef || "",
  notes: tender?.notes || "",
  key:
    tender?.reconciliationLineId ||
    `${tender?.tenderType || "line"}-${index}-${tender?.providerOrChannel || "default"}`,
});

const blankReconciliationLine = (index) => ({
  reconciliationLineId: null,
  tenderType: "CASH",
  providerOrChannel: "",
  actualAmount: "",
  settlementRef: "",
  slipAttachmentRef: "",
  notes: "",
  key: `blank-${Date.now()}-${index}`,
});

const CASH_MOVEMENT_OPTIONS = ["PAID_OUT", "CASH_DROP", "TIP_PAYOUT"];

const CashingUpSessionDetail = () => {
  const navigate = useNavigate();
  const { selectedStoreId } = useStore();
  const { userId: currentOperatorUserId, operatorName: currentOperatorName } =
    useCurrentOperator();
  const { cashSessionId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const tabFromUrl = searchParams.get("tab");
  const [activeTab, setActiveTab] = useState(() =>
    ["overview", "movements", "reconciliation"].includes(tabFromUrl)
      ? tabFromUrl
      : "overview",
  );
  const [movementModalOpen, setMovementModalOpen] = useState(false);
  const [movementForm, setMovementForm] = useState(movementInitialForm);
  const [movementFormErrors, setMovementFormErrors] = useState({});
  const [movementError, setMovementError] = useState("");
  const [reconciliationLines, setReconciliationLines] = useState([]);
  const [reconciliationLinesMessage, setReconciliationLinesMessage] =
    useState("");
  const [closeModalOpen, setCloseModalOpen] = useState(false);
  const [closeConfirmOpen, setCloseConfirmOpen] = useState(false);
  const [closeNotes, setCloseNotes] = useState("");
  const [lifecycleMessage, setLifecycleMessage] = useState("");

  const {
    data: session,
    isLoading,
    isError,
    error,
  } = useGetTillSessionQuery(cashSessionId, {
    skip: !cashSessionId,
  });
  const { data: tillsData } = useGetTillsQuery(
    { storeId: session?.storeId || selectedStoreId },
    { skip: !session?.storeId && !selectedStoreId },
  );
  const { data: usersData } = useGetUsersQuery();

  const tillById = useMemo(
    () =>
      new Map(
        (Array.isArray(tillsData) ? tillsData : []).map((till) => [
          till.tillId,
          till,
        ]),
      ),
    [tillsData],
  );
  const usersById = useMemo(
    () =>
      new Map(
        (Array.isArray(usersData) ? usersData : []).map((user) => [
          user.userId,
          user,
        ]),
      ),
    [usersData],
  );
  const getUserDisplayLabel = useCallback(
    (userId, fallback = "—") => {
      if (!userId) return fallback;
      if (userId === currentOperatorUserId && currentOperatorName) {
        return currentOperatorName;
      }
      const user = usersById.get(userId);
      return (
        user?.displayName ||
        user?.name ||
        user?.fullName ||
        user?.userName ||
        userId ||
        fallback
      );
    },
    [currentOperatorName, currentOperatorUserId, usersById],
  );
  const tillLabel = useMemo(() => {
    const till = tillById.get(session?.tillId);
    return (
      session?.tillName ||
      till?.tillName ||
      till?.tillCode ||
      session?.tillId ||
      "Unknown"
    );
  }, [session, tillById]);
  const normalizedSessionStatus = String(session?.status || "").toUpperCase();
  const canSubmitClose =
    Boolean(session?.countSubmittedAt) &&
    !["CLOSED", "REOPENED"].includes(normalizedSessionStatus);
  const {
    data: movementsData,
    isLoading: movementsLoading,
    isError: movementsIsError,
    error: movementsError,
    refetch: refetchMovements,
  } = useGetCashMovementsQuery(cashSessionId, {
    skip: !cashSessionId,
  });
  const {
    data: reconciliationData,
    isLoading: reconciliationLoading,
    isError: reconciliationIsError,
    error: reconciliationError,
    refetch: refetchReconciliation,
  } = useGetReconciliationQuery(cashSessionId, {
    skip: !cashSessionId,
  });
  const [createCashMovement, createMovementState] =
    useCreateCashMovementMutation();
  const [recalculateReconciliation, recalculateState] =
    useRecalculateReconciliationMutation();
  const [saveReconciliationLines, saveReconciliationState] =
    useSaveReconciliationLinesMutation();
  const [closeTillSession, closeTillSessionState] = useCloseTillSessionMutation();
  const handleCashSessionChanged = useCallback(
    (payload) => {
      const changedSessionId = payload?.cashSessionId || payload?.sessionId;
      if (!changedSessionId || changedSessionId === cashSessionId) {
        refetchMovements();
        refetchReconciliation();
      } else {
        refetchMovements();
      }
    },
    [cashSessionId, refetchMovements, refetchReconciliation],
  );

  usePosRealtime({
    onCashSessionChanged: handleCashSessionChanged,
  });

  const tabs = useMemo(
    () => [
      { id: "overview", label: "Overview" },
      { id: "movements", label: "Cash Movements" },
      // { id: "reconciliation", label: "Reconciliation" },
      // { id: "approvals", label: "Approvals" },
      // { id: "history", label: "History" },
    ],
    [],
  );

  useEffect(() => {
    const validTabs = new Set([
      "overview",
      "movements",
      // "reconciliation",
    ]);
    if (validTabs.has(tabFromUrl) && tabFromUrl !== activeTab) {
      setActiveTab(tabFromUrl);
    }
    if (!tabFromUrl && activeTab !== "overview") {
      setActiveTab("overview");
    }
  }, [activeTab, tabFromUrl]);

  const movements = useMemo(
    () =>
      (Array.isArray(movementsData) ? movementsData : []).filter((movement) =>
        isVisibleCashMovementStatus(movement?.status),
      ),
    [movementsData],
  );

  const tenderSummaries = useMemo(
    () =>
      Array.isArray(reconciliationData?.tenderSummaries)
        ? reconciliationData.tenderSummaries
        : [],
    [reconciliationData?.tenderSummaries],
  );

  useEffect(() => {
    if (!reconciliationData) return;
    const baseLines = tenderSummaries.map((tender, index) =>
      reconciliationLineFromTender(tender, index),
    );
    setReconciliationLines(baseLines);
  }, [reconciliationData, tenderSummaries]);

  const openMovementModal = () => {
    setMovementError("");
    setMovementFormErrors({});
    setMovementForm({
      ...movementInitialForm,
      movementType: "PAID_OUT",
    });
    setMovementModalOpen(true);
  };

  const resetMovementForm = () => {
    setMovementForm(movementInitialForm);
    setMovementFormErrors({});
    setMovementError("");
  };

  const handleMovementChange = (e) => {
    const { name, value, type, checked } = e.target;
    setMovementFormErrors({});
    setMovementError("");
    setMovementForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const validateMovementForm = () => {
    const errors = {};
    if (!movementForm.movementType) {
      errors.movementType = "Movement type is required.";
    }
    if (movementForm.amount === "" || Number.isNaN(Number(movementForm.amount))) {
      errors.amount = "Amount must be a valid number.";
    } else if (Number(movementForm.amount) <= 0) {
      errors.amount = "Amount must be greater than zero.";
    }
    return errors;
  };

  const submitMovement = async (e) => {
    e.preventDefault();
    const errors = validateMovementForm();
    if (Object.keys(errors).length > 0) {
      setMovementFormErrors(errors);
      return;
    }

    const payload = {
      movementType: movementForm.movementType,
      amount: Number(movementForm.amount),
      reasonCode: movementForm.reasonCode.trim() || null,
      createdByUserId: currentOperatorUserId || null,
    };

    try {
      await createCashMovement({
        cashSessionId,
        payload,
      }).unwrap();
      setMovementModalOpen(false);
      resetMovementForm();
      refetchMovements();
    } catch (err) {
      setMovementError(getErrorMessage(err, "Failed to create movement."));
    }
  };

  const handleReconciliationLineChange = (index, field, value) => {
    setReconciliationLinesMessage("");
    setReconciliationLines((prev) =>
      prev.map((line, idx) =>
        idx === index
          ? {
              ...line,
              [field]: value,
            }
          : line,
      ),
    );
  };

  const addReconciliationLine = () => {
    setReconciliationLines((prev) => [...prev, blankReconciliationLine(prev.length)]);
  };

  const removeReconciliationLine = (index) => {
    setReconciliationLines((prev) => prev.filter((_, idx) => idx !== index));
  };

  const handleRecalculate = async () => {
    try {
      await recalculateReconciliation({ cashSessionId }).unwrap();
      refetchReconciliation();
      setReconciliationLinesMessage("Reconciliation recalculated.");
    } catch (err) {
      setReconciliationLinesMessage(
        getErrorMessage(err, "Failed to recalculate reconciliation."),
      );
    }
  };

  const saveReconciliation = async () => {
    const payload = reconciliationLines.map((line) => ({
      reconciliationLineId: line.reconciliationLineId || null,
      tenderType: line.tenderType,
      providerOrChannel: line.providerOrChannel.trim() || null,
      actualAmount:
        line.actualAmount === "" || Number.isNaN(Number(line.actualAmount))
          ? null
          : Number(line.actualAmount),
      settlementRef: line.settlementRef.trim() || null,
      slipAttachmentRef: line.slipAttachmentRef.trim() || null,
      notes: line.notes.trim() || null,
    }));

    try {
      await saveReconciliationLines({
        cashSessionId,
        payload,
      }).unwrap();
      setReconciliationLinesMessage("Reconciliation lines saved.");
      refetchReconciliation();
    } catch (err) {
      setReconciliationLinesMessage(
        getErrorMessage(err, "Failed to save reconciliation lines."),
      );
    }
  };

  const openCloseModal = () => {
    setLifecycleMessage("");
    setCloseNotes("");
    setCloseConfirmOpen(false);
    setCloseModalOpen(true);
  };

  const requestCloseConfirmation = () => {
    setLifecycleMessage("");
    setCloseConfirmOpen(true);
  };

  const confirmCloseSession = async () => {
    try {
      await closeTillSession({
        cashSessionId,
        payload: {
          closedByUserId: null,
          notes: closeNotes.trim() || null,
        },
      }).unwrap();
      setCloseConfirmOpen(false);
      setCloseModalOpen(false);
      setLifecycleMessage("Session close submitted.");
    } catch (err) {
      setLifecycleMessage(getErrorMessage(err, "Failed to submit close."));
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <PageHeader
          title="Session Detail"
          subtitle="Loading till session..."
          actions={
            <Button size="sm" variant="secondary" onClick={() => navigate("/cash-management/till-sessions")}>
              Back
            </Button>
          }
        />
        <Card>
          <Alert tone="info">Loading session details...</Alert>
        </Card>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="space-y-6">
        <PageHeader
          title="Session Detail"
          subtitle="Unable to load session."
          actions={
            <Button size="sm" variant="secondary" onClick={() => navigate("/cash-management/till-sessions")}>
              Back
            </Button>
          }
        />
        <Card>
          <Alert tone="error">
            {getErrorMessage(error, "Failed to load session details.")}
          </Alert>
        </Card>
      </div>
    );
  }

  if (!session) {
    return (
      <div className="space-y-6">
        <PageHeader
          title="Session Detail"
          subtitle="Session not found."
          actions={
            <Button size="sm" variant="secondary" onClick={() => navigate("/cash-management/till-sessions")}>
              Back
            </Button>
          }
        />
        <Card>
          <Alert tone="warning">The selected till session could not be found.</Alert>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Session Detail"
        subtitle="Review the current till session and lifecycle metadata."
        actions={
          <div className="flex flex-wrap gap-2">
            <Button
              size="sm"
              variant="secondary"
              onClick={() => navigate("/cash-management/till-sessions")}
            >
              Back to Sessions
            </Button>
          </div>
        }
      />

      {lifecycleMessage && <Alert tone="info">{lifecycleMessage}</Alert>}

      <Card className="space-y-5">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold text-slate-900 dark:text-slate-100">
              {tillLabel}
            </h1>
            {session.localReferenceNo && (
              <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
                Session Ref: {session.localReferenceNo}
              </p>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            <Badge tone={getCashingUpStatusTone(session.status)}>
              {getCashingUpSessionStatusLabel(session.status)}
            </Badge>
            <Badge tone={getCashingUpStatusTone(session.approvalStatus)}>
              {getCashingUpApprovalStatusLabel(session.approvalStatus)}
            </Badge>
          </div>
        </div>

        {session.approvalStatus === "NOT_REQUIRED" && (
          <Alert tone="info">
            This session is fully closed. Any variance is retained for reporting and audit only.
          </Alert>
        )}

        <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div className="min-w-0 flex-1">
            <Tabs
              tabs={tabs}
              activeTab={activeTab}
              onChange={(tabId) => {
                setActiveTab(tabId);
                const nextParams = new URLSearchParams(searchParams);
                nextParams.set("tab", tabId);
                setSearchParams(nextParams, { replace: true });
              }}
            />
          </div>
          <div className="flex justify-end lg:pb-2">
            {activeTab === "movements" ? (
              <Button size="sm" onClick={openMovementModal}>
                Add Movement
              </Button>
            ) : null}
          </div>
        </div>

      {activeTab === "overview" && (
          <div className="space-y-5">
            <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
              <SessionDetailCard
                label="Opening Cash"
                value={formatCurrency(session.openingCash)}
              />
              <SessionDetailCard
                label="Total Sales"
                helperText="Cash + PDQ"
                value={formatCurrency(session.totalSales ?? 0)}
              />
              <SessionDetailCard
                label="Expected Cash"
                value={formatCurrency(session.expectedCash)}
              />
              <SessionDetailCard
                label="Actual Cash"
                value={formatCurrency(session.actualCash)}
              />
            </div>

            <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
              <SessionDetailCard
                label="Variance"
                value={formatCurrency(session.cashVariance)}
              />
              <SessionDetailCard
                label="Cash Refunds"
                value={formatCurrency(session.cashRefunds ?? 0)}
              />
              <SessionDetailCard
                label="PDQ Sales"
                value={formatCurrency(
                  Number(session.cardSales || getTenderSummaryAmount(session, "CARD")),
                )}
              />
              <SessionDetailCard
                label="PDQ Refunds"
                value={formatCurrency(session.cardRefunds ?? 0)}
              />
            </div>

            <div className="grid gap-3 md:grid-cols-2">
              <SessionDetailCard
                label="Opened At"
                value={session.openedAt ? new Date(session.openedAt).toLocaleString() : "-"}
              />
              <SessionDetailCard
                label="Closed At"
                value={session.closedAt ? new Date(session.closedAt).toLocaleString() : "-"}
              />
            </div>

          </div>
        )}

        {activeTab === "movements" && (
          <div className="space-y-4">
            {movementsLoading && (
              <p className="text-sm text-slate-500 dark:text-slate-400">
                Loading movements...
              </p>
            )}
            {movementsIsError && (
              <Alert tone="error">
                {getErrorMessage(
                  movementsError,
                  "Failed to load cash movements.",
                )}
              </Alert>
            )}
            {movementError && <Alert tone="error">{movementError}</Alert>}

            {!movementsLoading && !movementsIsError && (
              <Table
                columns={[
                  { key: "movementType", label: "Type" },
                  { key: "amount", label: "Amount" },
                  { key: "reasonCode", label: "Reason" },
                  { key: "createdByUserId", label: "Created By" },
                ]}
                rows={movements.map((movement) => {
                  const signedAmount = getMovementSignedAmount(
                    movement.movementType,
                    movement.amount,
                  );
                  const amountTone =
                    signedAmount > 0
                      ? "text-emerald-700 dark:text-emerald-300"
                      : signedAmount < 0
                        ? "text-rose-700 dark:text-rose-300"
                        : "text-slate-700 dark:text-slate-300";

                  return {
                    id: movement.cashMovementId,
                    movementType: (
                      <span className="font-semibold text-slate-900 dark:text-slate-100">
                        {getCashingUpMovementTypeLabel(movement.movementType)}
                      </span>
                    ),
                    amount: (
                      <span className={`font-semibold ${amountTone}`}>
                        {signedAmount > 0 ? "+" : signedAmount < 0 ? "-" : ""}
                        {formatCurrency(Math.abs(signedAmount))}
                      </span>
                    ),
                    reasonCode: movement.reasonCode || "-",
                    createdByUserId: getUserDisplayLabel(
                      movement.createdByUserId ||
                        movement.createdByUserName ||
                        movement.createdByName ||
                        movement.createdBy,
                      currentOperatorName || "-",
                    ),
                  };
                })}
                emptyText="No cash movements recorded for this session."
              />
            )}
          </div>
        )}

        {/* {activeTab === 'reconciliation' && (
          <div className="space-y-5">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <Alert tone="info">
                Use GET reconciliation to load the current breakdown. Recalculate
                when you want the backend to refresh the expected totals.
              </Alert>
              <div className="flex flex-wrap gap-2">
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={handleRecalculate}
                  disabled={recalculateState.isLoading}
                >
                  {recalculateState.isLoading ? "Recalculating..." : "Recalculate"}
                </Button>
                <Button
                  size="sm"
                  onClick={saveReconciliation}
                  disabled={saveReconciliationState.isLoading}
                >
                  {saveReconciliationState.isLoading ? "Saving..." : "Save Lines"}
                </Button>
              </div>
            </div>

            {reconciliationLoading && (
              <p className="text-sm text-slate-500 dark:text-slate-400">
                Loading reconciliation...
              </p>
            )}
            {reconciliationIsError && (
              <Alert tone="error">
                {getErrorMessage(
                  reconciliationError,
                  "Failed to load reconciliation summary.",
                )}
              </Alert>
            )}
            {reconciliationLinesMessage && (
              <Alert tone="info">{reconciliationLinesMessage}</Alert>
            )}

            {reconciliationData?.expectedCash && (
              <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
                <SessionDetailCard
                  label="Opening Cash"
                  value={formatCurrency(reconciliationData.expectedCash.openingCash)}
                />
                <SessionDetailCard
                  label="Cash Payments"
                  value={formatCurrency(reconciliationData.expectedCash.cashPayments)}
                />
                <SessionDetailCard
                  label="Expected Cash"
                  value={formatCurrency(reconciliationData.expectedCash.expectedCash)}
                />
                <SessionDetailCard
                  label="Paid Out"
                  value={formatCurrency(reconciliationData.expectedCash.paidOut)}
                />
                <SessionDetailCard
                  label="Cash Drop"
                  value={formatCurrency(reconciliationData.expectedCash.cashDrop)}
                />
                <SessionDetailCard
                  label="Tip Payout"
                  value={formatCurrency(reconciliationData.expectedCash.tipPayout)}
                />
              </div>
            )}

            <Card className="space-y-4">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <h3 className="text-base font-semibold text-slate-900 dark:text-slate-100">
                    Tender Summary
                  </h3>
                  <p className="text-sm text-slate-600 dark:text-slate-400">
                    View and edit tender-level settlement lines before saving.
                  </p>
                </div>
                <Button size="sm" variant="secondary" onClick={addReconciliationLine}>
                  Add Line
                </Button>
              </div>

              <div className="overflow-hidden">
                <table className="w-full table-fixed border-separate border-spacing-0 text-left text-sm">
                  <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500 dark:bg-slate-800/60 dark:text-slate-400">
                    <tr>
                      <th className="w-[18%] border-b border-slate-200 px-3 py-2 align-top dark:border-slate-700">Tender</th>
                      <th className="w-[24%] border-b border-slate-200 px-3 py-2 align-top dark:border-slate-700">Provider</th>
                      <th className="w-[15%] border-b border-slate-200 px-3 py-2 align-top dark:border-slate-700">Expected</th>
                      <th className="w-[15%] border-b border-slate-200 px-3 py-2 align-top dark:border-slate-700">Actual</th>
                      <th className="w-[14%] border-b border-slate-200 px-3 py-2 align-top dark:border-slate-700">Variance</th>
                      <th className="w-[14%] border-b border-slate-200 px-3 py-2 align-top dark:border-slate-700">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reconciliationLines.length === 0 ? (
                      <tr>
                        <td
                          colSpan={6}
                          className="border-b border-slate-200 px-3 py-4 text-slate-500 dark:border-slate-700 dark:text-slate-400"
                        >
                          No reconciliation lines yet. Use Recalculate or Add Line.
                        </td>
                      </tr>
                    ) : (
                      reconciliationLines.map((line, index) => {
                        const tenderSummary = tenderSummaries[index] || {};
                        const expectedAmount = Number(tenderSummary.expectedAmount || 0);
                        const actualAmount =
                          line.actualAmount === "" || Number.isNaN(Number(line.actualAmount))
                            ? null
                            : Number(line.actualAmount);
                        const variance = actualAmount === null ? expectedAmount : actualAmount - expectedAmount;

                        return (
                          <tr key={line.key || index} className="align-top">
                            <td className="border-b border-slate-200 px-3 py-3 align-top dark:border-slate-700">
                              <Select
                                value={line.tenderType}
                                onChange={(e) =>
                                  handleReconciliationLineChange(
                                    index,
                                    "tenderType",
                                    e.target.value,
                                  )
                                }
                                className="w-full"
                              >
                                <option value="CASH">CASH</option>
                                <option value="CARD">CARD</option>
                                <option value="UPI">UPI</option>
                                <option value="WALLET">WALLET</option>
                                <option value="OTHER">OTHER</option>
                              </Select>
                            </td>
                            <td className="border-b border-slate-200 px-3 py-3 align-top dark:border-slate-700">
                              <Input
                                value={line.providerOrChannel}
                                onChange={(e) =>
                                  handleReconciliationLineChange(
                                    index,
                                    "providerOrChannel",
                                    e.target.value,
                                  )
                                }
                                placeholder="Terminal / channel"
                                className="w-full"
                              />
                            </td>
                            <td className="border-b border-slate-200 px-3 py-3 align-top dark:border-slate-700">
                              {formatCurrency(expectedAmount)}
                            </td>
                            <td className="border-b border-slate-200 px-3 py-3 align-top dark:border-slate-700">
                              <Input
                                type="number"
                                value={line.actualAmount}
                                onChange={(e) =>
                                  handleReconciliationLineChange(
                                    index,
                                    "actualAmount",
                                    e.target.value,
                                  )
                                }
                                placeholder="0.00"
                                className="w-full"
                              />
                            </td>
                            <td className="border-b border-slate-200 px-3 py-3 align-top dark:border-slate-700">
                              <span
                                className={
                                  variance >= 0
                                    ? "font-semibold text-emerald-700 dark:text-emerald-300"
                                    : "-"
                                }
                              >
                                {formatCurrency(variance)}
                              </span>
                            </td>
                            <td className="border-b border-slate-200 px-3 py-3 align-top dark:border-slate-700">
                              <Button
                                size="sm"
                                variant="secondary"
                                className="whitespace-nowrap"
                                onClick={() => removeReconciliationLine(index)}
                              >
                                Remove
                              </Button>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </Card>

            {saveReconciliationState.isError && (
              <Alert tone="error">
                {getErrorMessage(
                  saveReconciliationState.error,
                  "Failed to save reconciliation lines.",
                )}
              </Alert>
            )}
          </div>
        )} */}

        {/* {activeTab === "approvals" && (
          <div className="space-y-4">
            <Alert tone="info">
              Use these actions to submit the session for close approval, update
              approval status, or reopen the session if a correction is needed.
            </Alert>

            {lifecycleMessage && <Alert tone="info">{lifecycleMessage}</Alert>}

            <div className="flex flex-wrap gap-2">
              <Button
                size="sm"
                onClick={openCloseModal}
                disabled={
                  closeTillSessionState.isLoading || !canSubmitClose
                }
              >
                Close Session
              </Button>
              <Button
                size="sm"
                variant="secondary"
                onClick={() => openApprovalModal(true)}
                disabled={
                  approveTillSessionState.isLoading ||
                  session.approvalStatus === "APPROVED"
                }
              >
                Approve
              </Button>
              <Button
                size="sm"
                variant="secondary"
                onClick={() => openApprovalModal(false)}
                disabled={
                  approveTillSessionState.isLoading ||
                  session.approvalStatus === "REJECTED"
                }
              >
                Reject
              </Button>
              <Button
                size="sm"
                variant="secondary"
                onClick={openReopenModal}
                disabled={reopenTillSessionState.isLoading}
              >
                Reopen
              </Button>
            </div>

            <div className="grid gap-3 md:grid-cols-2">
              <SessionDetailCard
                label="Count Submitted At"
                value={
                  session.countSubmittedAt
                    ? new Date(session.countSubmittedAt).toLocaleString()
                    : "-"
                }
              />
              <SessionDetailCard
                label="Approval Notes"
                value={session.approvalNotes || "-"}
              />
              <SessionDetailCard
                label="Reopened At"
                value={
                  session.reopenedAt
                    ? new Date(session.reopenedAt).toLocaleString()
                    : "-"
                }
              />
              <SessionDetailCard
                label="Close Version"
                value={session.closeVersion ?? "-"}
              />
            </div>
          </div>
        )}

        {activeTab === "history" && (
          <div className="space-y-5">
            <Alert tone="info">
              Session history shows lifecycle timestamps and audit metadata for
              review.
            </Alert>

            <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
              <SessionDetailCard
                label="Opened At"
                value={
                  session.openedAt
                    ? new Date(session.openedAt).toLocaleString()
                    : "-"
                }
              />
              <SessionDetailCard
                label="Count Submitted At"
                value={
                  session.countSubmittedAt
                    ? new Date(session.countSubmittedAt).toLocaleString()
                    : "-"
                }
              />
              <SessionDetailCard
                label="Approved At"
                value={
                  session.approvedAt
                    ? new Date(session.approvedAt).toLocaleString()
                    : "-"
                }
              />
              <SessionDetailCard
                label="Closed At"
                value={
                  session.closedAt
                    ? new Date(session.closedAt).toLocaleString()
                    : "-"
                }
              />
              <SessionDetailCard
                label="Reopened At"
                value={
                  session.reopenedAt
                    ? new Date(session.reopenedAt).toLocaleString()
                    : "-"
                }
              />
              <SessionDetailCard
                label="Approval Status"
                value={getCashingUpApprovalStatusLabel(session.approvalStatus)}
              />
              <SessionDetailCard
                label="Approval Notes"
                value={session.approvalNotes || "-"}
              />
              <SessionDetailCard
                label="Close Version"
                value={session.closeVersion ?? "-"}
              />
            </div>
          </div>
        )} */}
      </Card>

      <Modal
        isOpen={movementModalOpen}
        onClose={() => {
          setMovementModalOpen(false);
          resetMovementForm();
        }}
        title="Add Cash Movement"
        confirmText={createMovementState.isLoading ? "Saving..." : "Save Movement"}
        onConfirm={submitMovement}
        size="xl"
        isLoading={createMovementState.isLoading}
      >
        <div className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            <Select
              label="Movement Type"
              name="movementType"
              value={movementForm.movementType}
              onChange={handleMovementChange}
              error={movementFormErrors.movementType}
              required
            >
              <option value="">Select type</option>
              {CASH_MOVEMENT_OPTIONS.map((type) => (
                <option key={type} value={type}>
                  {getCashingUpMovementTypeLabel(type)}
                </option>
              ))}
            </Select>
            <Input
              label="Amount"
              name="amount"
              type="number"
              value={movementForm.amount}
              onChange={handleMovementChange}
              error={movementFormErrors.amount}
              required
            />
          </div>

          <Input
            label="Reason Code"
            name="reasonCode"
            value={movementForm.reasonCode}
            onChange={handleMovementChange}
          />

          {movementError && <Alert tone="error">{movementError}</Alert>}

          {createMovementState.isError && (
            <Alert tone="error">
              {getErrorMessage(
                createMovementState.error,
                "Failed to save cash movement.",
              )}
            </Alert>
          )}
        </div>
      </Modal>


      <Modal
        isOpen={closeModalOpen}
        onClose={() => {
          setCloseConfirmOpen(false);
          setCloseModalOpen(false);
          setCloseNotes("");
        }}
        title="Close Session"
        confirmText="Close Session"
        onConfirm={requestCloseConfirmation}
        isLoading={closeTillSessionState.isLoading}
        align="center"
      >
        <div className="space-y-4">
          <Textarea
            label="Close Notes"
            value={closeNotes}
            onChange={(e) => setCloseNotes(e.target.value)}
            helper="Optional notes that will be stored with the close action."
            rows={4}
          />
          {closeTillSessionState.isError && (
            <Alert tone="error">
              {getErrorMessage(
                closeTillSessionState.error,
                "Failed to submit close.",
              )}
            </Alert>
          )}
        </div>
      </Modal>

      <Modal
        isOpen={closeConfirmOpen}
        onClose={() => setCloseConfirmOpen(false)}
        title="Confirm Close Session"
        confirmText={
          closeTillSessionState.isLoading ? "Closing..." : "Close Session"
        }
        cancelText="Cancel"
        onConfirm={confirmCloseSession}
        isLoading={closeTillSessionState.isLoading}
        size="md"
        align="center"
        zIndexClassName="z-[60]"
        showCancel
        showCloseButton
      >
        <div className="space-y-4">
          <p className="text-sm leading-6 text-slate-600 dark:text-slate-400">
            Are you sure you want to close the {tillLabel} session?
          </p>
          <Alert tone="warning">
            The session will close even if a variance exists.
          </Alert>
          {closeTillSessionState.isError && (
            <Alert tone="error">
              {getErrorMessage(
                closeTillSessionState.error,
                "Failed to submit close.",
              )}
            </Alert>
          )}
        </div>
      </Modal>
    </div>
  );
};

export default CashingUpSessionDetail;






